/**
 * CompactMessageList — the "By List" view: a dense, virtualized data table
 * rendered in the wide main (reading-pane) panel like the By Sender / By
 * Category overviews so the per-row action buttons have room.
 *
 * It draws the column header — sortable labels (click to cycle asc → desc →
 * off), full-height drag handles that resize Subject / Correspondents (widths
 * persisted per-device via {@link useByListColumnWidths}), and a settings
 * popover to show/hide columns (persisted via {@link useByListColumnVisibility}).
 * Sorting itself is owned by the caller (it holds the messages): the header
 * reports clicks through `onToggleSort` and reflects `sort`. The feed reports
 * its top row via `onRangeChange` and restores it via `initialTopMostItemIndex`,
 * so returning from an opened message lands back where the list was.
 */

import { useRef } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { ChevronUp, ChevronDown, ChevronsUpDown, Settings2 } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { CompactMessageRow } from './CompactMessageRow';
import {
  useByListColumnWidths,
  useByListColumnVisibility,
  BY_LIST_COLUMN_BOUNDS,
  type ByListColumnVisibility,
} from '../hooks/useEmailLayoutPrefs';
import type { EmailMessageDto } from '@/lib/api';

// `scrollbarGutter: stable` reserves the scrollbar lane so an auto-hiding /
// overlay scrollbar can't expand over the row's right-edge actions (paired with
// the `mail-list-scrollbar` class, which forces a classic non-overlay bar).
const LIST_STYLE = { height: '100%', scrollbarGutter: 'stable' } as const;

// The leading gutter holds select · star · attachment; its width is derived from
// what's actually shown so columns stay aligned as indicators are toggled.
const LEAD_PAD = 12; // pl-2 + pr-1
const CHECKBOX_W = 18; // checkbox + gap
const STAR_W = 22; // star button + gap
const ATTACH_W = 18; // attachment indicator + gap

export type ByListSortColumn = 'subject' | 'correspondents' | 'date';
export interface ByListSort {
  column: ByListSortColumn | null;
  dir: 'asc' | 'desc';
}

type VisibilityOption = { key: keyof ByListColumnVisibility; label: string };
const COLUMN_OPTIONS: VisibilityOption[] = [
  { key: 'star', label: 'Star' },
  { key: 'attachments', label: 'Attachment' },
  { key: 'subject', label: 'Subject' },
  { key: 'correspondents', label: 'Correspondents' },
  { key: 'date', label: 'Date' },
];
const ACTION_OPTIONS: VisibilityOption[] = [
  { key: 'reply', label: 'Reply' },
  { key: 'replyAll', label: 'Reply all' },
  { key: 'forward', label: 'Forward' },
  { key: 'spam', label: 'Spam' },
  { key: 'archive', label: 'Archive' },
  { key: 'delete', label: 'Delete' },
];

/** A full-height drag handle at a header cell's right edge (visible + grabbable). */
function ColumnDragHandle({
  width,
  min,
  max,
  onResize,
  label,
}: {
  width: number;
  min: number;
  max: number;
  onResize: (next: number) => void;
  label: string;
}) {
  const drag = useRef<{ startX: number; startWidth: number } | null>(null);
  const clamp = (n: number) => Math.min(max, Math.max(min, Math.round(n)));
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${label} column`}
      aria-valuenow={Math.round(width)}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      className="group absolute inset-y-0 right-0 z-10 flex w-2 cursor-col-resize touch-none justify-end"
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        drag.current = { startX: e.clientX, startWidth: width };
      }}
      onPointerMove={(e) => {
        const d = drag.current;
        if (!d) return;
        onResize(clamp(d.startWidth + (e.clientX - d.startX)));
      }}
      onPointerUp={(e) => {
        drag.current = null;
        try {
          (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
        } catch {
          // capture may already be released
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          onResize(clamp(width - 16));
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          onResize(clamp(width + 16));
        }
      }}
    >
      <div
        aria-hidden
        className="h-full w-px bg-border transition-colors group-hover:w-0.5 group-hover:bg-primary group-focus-visible:w-0.5 group-focus-visible:bg-primary"
      />
    </div>
  );
}

/** A sortable header label — click to cycle the sort; shows the active direction. */
function SortableHeader({
  label,
  column,
  sort,
  onToggleSort,
}: {
  label: string;
  column: ByListSortColumn;
  sort: ByListSort;
  onToggleSort: (c: ByListSortColumn) => void;
}) {
  const active = sort.column === column;
  return (
    <button
      type="button"
      onClick={() => onToggleSort(column)}
      aria-label={`Sort by ${label}${active ? (sort.dir === 'asc' ? ', ascending' : ', descending') : ''}`}
      className="flex min-w-0 flex-1 items-center gap-1 pr-2 text-left hover:text-foreground"
    >
      <span className="truncate">{label}</span>
      {active ? (
        sort.dir === 'asc' ? (
          <ChevronUp className="h-3 w-3 shrink-0" />
        ) : (
          <ChevronDown className="h-3 w-3 shrink-0" />
        )
      ) : (
        <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-30" />
      )}
    </button>
  );
}

export interface CompactMessageListProps {
  messages: EmailMessageDto[];
  showRecipients: boolean;
  previewLines: 1 | 2 | 3;
  selectionMode: boolean;
  selectedIds: ReadonlySet<string>;
  sort: ByListSort;
  onToggleSort: (column: ByListSortColumn) => void;
  /** Screener view: rows offer Approve / Reject sender instead of star + actions. */
  screenerView?: boolean;
  onSelect: (messageId: string, shiftKey: boolean) => void;
  onOpenFull: (m: EmailMessageDto) => void;
  onStar: (m: EmailMessageDto) => void;
  onReply: (messageId: string, all: boolean) => void;
  onForward: (messageId: string) => void;
  onReportSpam: (accountId: string, messageId: string) => void;
  onArchive: (m: EmailMessageDto) => void;
  onTrash: (m: EmailMessageDto) => void;
  onApproveSender?: (accountId: string, fromAddress: string) => void;
  onRejectSender?: (accountId: string, fromAddress: string) => void;
  formatDate: (iso: string) => string;
  /** Restore the top row when the list remounts (return from a read message). */
  initialTopMostItemIndex?: number;
  /** Report the current top row so the caller can restore it later. */
  onRangeChange?: (startIndex: number) => void;
}

export function CompactMessageList({
  messages,
  showRecipients,
  previewLines,
  selectionMode,
  selectedIds,
  sort,
  onToggleSort,
  screenerView = false,
  onSelect,
  onOpenFull,
  onStar,
  onReply,
  onForward,
  onReportSpam,
  onArchive,
  onTrash,
  onApproveSender,
  onRejectSender,
  formatDate,
  initialTopMostItemIndex,
  onRangeChange,
}: CompactMessageListProps) {
  const [widths, setWidths] = useByListColumnWidths();
  const [visibility, setVisibility] = useByListColumnVisibility();
  const leadWidth =
    LEAD_PAD +
    (selectionMode ? CHECKBOX_W : 0) +
    (visibility.star ? STAR_W : 0) +
    (visibility.attachments ? ATTACH_W : 0);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Column header: sortable labels, drag handles (Subject/Correspondents),
          and a settings popover to show/hide columns. */}
      <div className="flex h-8 items-stretch border-b bg-muted/40 pr-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground select-none">
        <div className="shrink-0" style={{ width: leadWidth }} aria-hidden />
        {visibility.subject && (
          <div className="relative flex shrink-0 items-center" style={{ width: widths.subject }}>
            <SortableHeader
              label="Subject"
              column="subject"
              sort={sort}
              onToggleSort={onToggleSort}
            />
            <ColumnDragHandle
              width={widths.subject}
              min={BY_LIST_COLUMN_BOUNDS.subject.min}
              max={BY_LIST_COLUMN_BOUNDS.subject.max}
              onResize={(w) => setWidths({ subject: w })}
              label="subject"
            />
          </div>
        )}
        {visibility.correspondents && (
          <div
            className="relative flex shrink-0 items-center"
            style={{ width: widths.correspondents }}
          >
            <SortableHeader
              label="Correspondents"
              column="correspondents"
              sort={sort}
              onToggleSort={onToggleSort}
            />
            <ColumnDragHandle
              width={widths.correspondents}
              min={BY_LIST_COLUMN_BOUNDS.correspondents.min}
              max={BY_LIST_COLUMN_BOUNDS.correspondents.max}
              onResize={(w) => setWidths({ correspondents: w })}
              label="correspondents"
            />
          </div>
        )}
        {visibility.date && (
          <div className="relative flex shrink-0 items-center" style={{ width: widths.date }}>
            <SortableHeader label="Date" column="date" sort={sort} onToggleSort={onToggleSort} />
            <ColumnDragHandle
              width={widths.date}
              min={BY_LIST_COLUMN_BOUNDS.date.min}
              max={BY_LIST_COLUMN_BOUNDS.date.max}
              onResize={(w) => setWidths({ date: w })}
              label="date"
            />
          </div>
        )}
        <span className="flex-1" />
        <div className="flex shrink-0 items-center">
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label="Column settings"
                title="Show / hide columns"
                className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                <Settings2 className="h-3.5 w-3.5" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="max-h-[70vh] w-52 overflow-y-auto p-2">
              <p className="mb-1 px-1 text-xs font-semibold text-muted-foreground">Columns</p>
              {COLUMN_OPTIONS.map((opt) => (
                <label
                  key={opt.key}
                  className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-accent"
                >
                  <Checkbox
                    className="h-4 w-4"
                    checked={visibility[opt.key]}
                    onCheckedChange={(c) => setVisibility({ [opt.key]: c })}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
              <p className="mb-1 mt-2 px-1 text-xs font-semibold text-muted-foreground">Actions</p>
              {ACTION_OPTIONS.map((opt) => (
                <label
                  key={opt.key}
                  className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-accent"
                >
                  <Checkbox
                    className="h-4 w-4"
                    checked={visibility[opt.key]}
                    onCheckedChange={(c) => setVisibility({ [opt.key]: c })}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </PopoverContent>
          </Popover>
        </div>
      </div>
      <div className="min-h-0 flex-1">
        {messages.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            No messages here.
          </div>
        ) : (
          <Virtuoso
            className="mail-list-scrollbar"
            style={LIST_STYLE}
            data={messages}
            computeItemKey={(_i, m) => m.messageId}
            initialTopMostItemIndex={Math.min(
              Math.max(0, initialTopMostItemIndex ?? 0),
              Math.max(0, messages.length - 1),
            )}
            rangeChanged={(range) => onRangeChange?.(range.startIndex)}
            itemContent={(_i, m) => (
              <CompactMessageRow
                message={m}
                showRecipients={showRecipients}
                previewLines={previewLines}
                selectionMode={selectionMode}
                selected={selectedIds.has(m.messageId)}
                leadWidth={leadWidth}
                subjectWidth={widths.subject}
                correspondentsWidth={widths.correspondents}
                dateWidth={widths.date}
                visibility={visibility}
                screenerView={screenerView}
                onSelect={(shiftKey) => onSelect(m.messageId, shiftKey)}
                onOpenFull={() => onOpenFull(m)}
                onStar={() => onStar(m)}
                onReply={() => onReply(m.messageId, false)}
                onReplyAll={() => onReply(m.messageId, true)}
                onForward={() => onForward(m.messageId)}
                onReportSpam={() => onReportSpam(m.accountId, m.messageId)}
                onArchive={() => onArchive(m)}
                onTrash={() => onTrash(m)}
                onApproveSender={() => onApproveSender?.(m.accountId, m.fromAddress)}
                onRejectSender={() => onRejectSender?.(m.accountId, m.fromAddress)}
                formatDate={formatDate}
              />
            )}
          />
        )}
      </div>
    </div>
  );
}
